
import { useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Button,
  Card,
  IconButton,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useReactToPrint } from "react-to-print";

const DetailedTableModal = ({
  show,
  onClose,
  data,
  renderHeaders,
  formatName,
  classes,
  pdfHeight,
  pdfTitle,
  headers,
  showSplitTables = false,
}) => {
  const pdfRef = useRef();
  const maxCardDimensions = { width: 200, height: 135 };

  const exportToPDF = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: `${pdfTitle}`,
    removeAfterPrint: true,
    pageStyle: `
        @page {
          size: 380mm ${pdfHeight}mm;
          margin-left: 10mm;
          margin-right: 10mm;
          margin-bottom: 5mm;
          margin-top: 15mm;
        }
        @media print {
        .parent-container {
    overflow: hidden;
  }
          body {
            -webkit-print-color-adjust: exact; /* Ensures accurate color printing */
          }
          .page-footer {
            position: fixed;
            bottom: 10mm;
            left: 10mm;
            right: 10mm;
            font-size: 10pt;
            color: #777;
            text-align: center;
          }
        }
      `,
  });

  const half = showSplitTables ? Math.ceil(data.length / 2) : data.length; // Split point
  const firstHalf = data.slice(0, half); // First 4 rows
  const secondHalf = showSplitTables ? data.slice(half) : []; // Remaining 4 rows

  return (
    <Modal open={show} onClose={onClose} id="metric_detail">
      <Box
        sx={{
          width: "90%",
          maxWidth: "1400px",
          backgroundColor: "white",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          position: "absolute",
          top: "5vh",
          left: "50%",
          transform: "translateX(-50%)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6">Detailed Table View </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={exportToPDF}
              sx={{ textTransform: "none" }}
            >
              <FontAwesomeIcon
                icon={faDownload}
                style={{ marginRight: "5px" }}
              />
              Download as PDF
            </Button>
            <IconButton id="close_id" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Box>
        </Box>

        {(!showSplitTables || showSplitTables === "shiftKey") && (
          <div
            style={{ flex: 1, overflowY: "auto", pb: 2 }}
            className="card-container"
            id="print-pdf"
            ref={pdfRef}
          >
            <Box sx={{ paddingBottom: "40px" }}>
              <Table>
                {renderHeaders()}
                <TableBody>
                  {firstHalf.map((mainItem, index) => (
                    <TableRow key={index}>
                      <TableCell className={classes.attribute_headings}>
                        {formatName(mainItem.name)}
                      </TableCell>
                      {headers?.map((brand, idx) => {
                        const detailInfo = mainItem?.scores || mainItem?.brands;
                        const brandScore = detailInfo?.find(
                          (item) => item.name === brand
                        );
                        const score = brandScore?.score || brandScore?.rating;
                        let color = "#dc3545";
                        if (score >= 7) color = "#2196F3";
                        if (score >= 9) color = "#198754";

                        return (
                          <TableCell
                            key={idx}
                            className={classes.attribute_score}
                          >
                            {brandScore ? (
                              <Card
                                sx={{
                                  width: maxCardDimensions.width,
                                  height: maxCardDimensions.height,
                                }}
                              >
                                <Typography
                                  sx={{
                                    padding: "1px",
                                    fontWeight: "bold",
                                    color,
                                  }}
                                  variant="body1"
                                >
                                  {Number(score).toFixed(0)}
                                </Typography>
                                <Typography sx={{ fontSize: "9.8px", mt: 0.2 }}>
                                  {brandScore.explanation}
                                </Typography>
                              </Card>
                            ) : (
                              <Typography>-</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </div>
        )}

        {showSplitTables &&
          showSplitTables === "altKey" &&
          secondHalf.length && (
            <div
              style={{ flex: 1, overflowY: "auto", pb: 2 }}
              className="card-container"
              id="print-pdf-2"
            >
              <Box>
                <Table>
                  {renderHeaders()}
                  <TableBody>
                    {secondHalf.map((mainItem, index) => (
                      <TableRow key={index + half}>
                        <TableCell className={classes.attribute_headings}>
                          {formatName(mainItem.name)}
                        </TableCell>
                        {headers?.map((brand, idx) => {
                          const detailInfo =
                            mainItem?.scores || mainItem?.brands;
                          const brandScore = detailInfo?.find(
                            (item) => item.name === brand
                          );
                          const score = brandScore?.score || brandScore?.rating;
                          let color = "#dc3545";
                          if (score >= 7) color = "#2196F3";
                          if (score >= 9) color = "#198754";

                          return (
                            <TableCell
                              key={idx}
                              className={classes.attribute_score}
                            >
                              {brandScore ? (
                                <Card
                                  sx={{
                                    width: maxCardDimensions.width,
                                    height: maxCardDimensions.height,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      padding: "1px",
                                      fontWeight: "bold",
                                      color,
                                    }}
                                    variant="body1"
                                  >
                                    {Number(score).toFixed(0)}
                                  </Typography>
                                  <Typography
                                    sx={{ fontSize: "9.8px", mt: 0.2 }}
                                  >
                                    {brandScore.explanation}
                                  </Typography>
                                </Card>
                              ) : (
                                <Typography>-</Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </div>
          )}

      </Box>
    </Modal>
  );
};

export function ReportScreenShot({
  data,
  renderHeaders,
  formatName,
  classes,
  headers,
}) {
  const half = Math.ceil(data.length / 2); // Split point
  const firstHalf = data.slice(0, half); // First 4 rows
  const secondHalf = data.slice(half); // Remaining 4 rows
  const maxCardDimensions = { width: 200, height: 135 };



  return (
    <div id="brand_pillars_detail">
      {[firstHalf, secondHalf].map((ele, index) => {
        return (
          <div
            style={{ width: '100%', height: '100%', pb: 2, display: "none" }}
            id={`brand_pillars_detail_${index + 1}`}
          >
            <Box sx={{ paddingBottom: "40px" }}>
              <Table>
                {renderHeaders(true)}
                <TableBody>
                  {ele.map((mainItem, index) => (
                    <TableRow key={index}>
                      <TableCell className={classes.attribute_headings}>
                        {formatName(mainItem.name)}
                      </TableCell>
                      {headers?.map((brand, idx) => {
                        const detailInfo = mainItem?.scores || mainItem?.brands;
                        const brandScore = detailInfo?.find((item) =>
                          item.name
                            ?.toLowerCase()
                            .includes(brand?.toLowerCase())
                        );
                        const score = brandScore?.score || brandScore?.rating;
                        let color = "#dc3545";
                        if (score >= 7) color = "#2196F3";
                        if (score >= 9) color = "#198754";

                        return (
                          <TableCell
                            key={idx}
                            className={classes.attribute_score}
                          >
                            {brandScore ? (
                              <Card
                                sx={{
                                  width: maxCardDimensions.width,
                                  height: maxCardDimensions.height,
                                }}
                              >
                                <Typography
                                  sx={{
                                    padding: "1px",
                                    fontWeight: "bold",
                                    color,
                                  }}
                                  variant="body1"
                                >
                                  {Number(score).toFixed(0)}
                                </Typography>
                                <Typography sx={{ fontSize: "9.8px", mt: 0.2 }}>
                                  {brandScore.explanation}
                                </Typography>
                              </Card>
                            ) : (
                              <Typography>-</Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </div>
        );
      })}
    </div>
  );
}

export default DetailedTableModal;

